import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
  ErrorMessage,
  FieldArray,
  FieldArrayRenderProps,
  FormikErrors,
  FormikTouched,
} from 'formik';
import { Button, Form, ProgressBar } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import {
  ProgramCustomField,
  ProgramDialogAction,
  ProgramEditFormikModel,
  ProgramTask,
  ProgramType,
  ResolvedCampaign,
} from '../../types';
import { errorToString } from '../../../util/utils';
import { CustomFieldMemoised } from '../CustomField';
import { Tasks } from '../tasks/Tasks';
import { hexToRGB, isSocialPostingProgramType, ModalDropdownMenuZIndex } from '../../util';
import { FormulaScopeUpdater } from '../FormulaScopeUpdater';
import clsx from 'clsx';
// eslint-disable-next-line import/no-cycle
import { CampaignProgramsTable } from '../CampaignProgramsTable';
import { ColoredSelect } from '../ColoredSelect';
import { DivButton } from '../../../commons/components/DivButton';
import {
  BinIcon,
  CloseIcon,
  CopyIcon,
  DividerIcon,
  LinkIcon,
  PlusSmallIcon,
} from '../../../assets/icons';
import { StyleVariables } from '../../../commons/styleConstants';
import { Attachments } from '../attachments/Attachments';
import { AppRoutesHelper } from '../../../router/AppRoutes';
import { useNotification } from '../../../notification/notificationsContext';
import useAuth from '../../../auth/authContext';
import { Permission } from '../../../auth/permissions';
import { PleaseRelogin } from '../../../auth/PleaseRelogin';
import { TaskArrayHelpers } from '../tasks/CellProps';
import { AddProgramPermissionHelper, ProgramPermissionHelper } from '../../programPermissionHelper';
import { useResolvedCampaigns } from '../../hooks/useResolvedCampaigns';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { ProgramDragDropContext } from '../ProgramDragDropContext';
import { PROGRAM_TYPE_BACKGROUND_ALPHA } from '../../../program-type/util';
import { FooterError } from '../../../commons/components/FooterError';
import { useIsSmallerThanTablet } from '../../../responsive/hooks/hooks';
import { CampaignColor } from './CampaignColor';
import { ProgramEditStatus } from './ProgramEditStatus';
import { ProgramEditOwner } from './ProgramEditOwner';
import { ProgramEditCampaign } from './ProgramEditCampaign';
import { ProgramEditDates } from './ProgramEditDates';
import { ProgramEditBudget } from './ProgramEditBudget';
import { ProgramEditVendor } from './ProgramEditVendor';
import { ProgramEditDescription } from './ProgramEditDescription';
import { ProgramEditName } from './ProgramEditName';
import { ProgramEditSocialPosting } from './ProgramEditSocialPosting';

const newProgramCustomField = (): ProgramCustomField => ({
  localId: uuidv4(),
  type: 'text',
  name: '',
  value: '',
  isNew: true,
});

const getFirstValidationError = (
  touched: FormikTouched<ProgramEditFormikModel>,
  errors: FormikErrors<ProgramEditFormikModel>
) => {
  const touchedSet = new Set(Object.keys(touched));
  const filteredErrors = Object.entries(errors).filter(([key]) => touchedSet.has(key));
  const requiredFields = filteredErrors
    .filter(([, value]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const v: any = value;
      return (
        (typeof v === 'string' && v.toLowerCase().includes('required')) ||
        JSON.stringify(value).toLowerCase().includes('not be one of')
      );
    })
    .map(([key]) => key);

  if (requiredFields.length) {
    return `Fill in the required fields: ${requiredFields.map((f) => _.capitalize(f)).join(', ')}`;
  }

  if (errors.customFields || errors.formulaScope) {
    return 'Custom fields require proper names and values';
  }

  if (errors.customFieldsNameSums) {
    return 'Custom fields names must be unique';
  }

  const wrongValuesFields = filteredErrors.map(([key]) => _.capitalize(key));
  if (wrongValuesFields.length) {
    return `Fix the values of the following fields: ${wrongValuesFields.join(', ')}`;
  }

  return null;
};

export const ProgramEditFormik = ({
  touched,
  errors,
  isValid,
  handleSubmit,
  handleChange,
  handleBlur,
  values,
  setFieldValue,
  setFieldTouched,
  initialValues,
  programTypes,
  action,
  disabled,
  createError,
  updateError,
  deleteError,
  handleHideClick,
  handleRefetchClick,
  handleDeleteClick,
  handleCopyClick,
  handleConvertTaskToProgramClick,
  handleShowCampaignClick,
  scrollToTaskId,
  programPermissionHelper,
  addProgramPermissionHelper,
  uploadProgress,
}: {
  touched: FormikTouched<ProgramEditFormikModel>;
  errors: FormikErrors<ProgramEditFormikModel>;
  isValid: boolean;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
  handleChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void;
  handleBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  values: ProgramEditFormikModel;
  setFieldValue: (
    field: keyof ProgramEditFormikModel,
    value: unknown,
    shouldValidate?: boolean | undefined
  ) => void;
  setFieldTouched: (
    field: keyof ProgramEditFormikModel,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => void;
  initialValues: ProgramEditFormikModel;
  disabled: boolean;
  programTypes: ProgramType[];
  createError: unknown;
  updateError: unknown;
  deleteError: unknown;
  handleHideClick: () => void;
  handleRefetchClick: () => Promise<void>;
  handleDeleteClick: () => Promise<void>;
  handleCopyClick: () => void;
  handleConvertTaskToProgramClick: (task: ProgramTask) => void;
  handleShowCampaignClick: (c: ResolvedCampaign) => void;
  action: ProgramDialogAction;
  scrollToTaskId: string | null;
  programPermissionHelper: ProgramPermissionHelper;
  addProgramPermissionHelper: AddProgramPermissionHelper;
  uploadProgress?: number;
}) => {
  const {
    state: { user },
  } = useAuth();

  const handleBlurMemoized = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => handleBlur(event),
    [handleBlur]
  );
  const inputNameRef = useRef<HTMLInputElement>();
  useEffect(() => {
    if (!disabled && scrollToTaskId === null) inputNameRef.current?.focus();
  }, [action, disabled, scrollToTaskId]);
  const { notifySuccess } = useNotification();
  const { data: campaigns } = useResolvedCampaigns();

  const isCampaign = initialValues.programKind === 'CAMPAIGN';

  const [submitButtonTooltipId] = useState<string>(
    `submit-button-tooltip__${initialValues.programKind}-${uuidv4()}`
  );

  const [taskInFocusOrderId, setTaskInFocusOrderId] = useState<number | null>(null);
  const wrapArrayHelpers = (internal: TaskArrayHelpers): TaskArrayHelpers => ({
    push: (value) => {
      setTaskInFocusOrderId(value.orderId);
      internal.push(value);
    },
    insert: (index, value) => {
      setTaskInFocusOrderId(value.orderId);
      internal.insert(index, value);
    },
    remove: (index) => {
      setTaskInFocusOrderId(null);
      internal.remove(index);
    },
    replace: (index, value) => {
      setTaskInFocusOrderId(null);
      internal.replace(index, value);
    },
    move: (from, to) => {
      setTaskInFocusOrderId(null);
      internal.move(from, to);
    },
  });

  const [initialCustomFields, setInitialCustomFields] = useState(initialValues.customFields);
  useEffect(() => setInitialCustomFields(initialValues.customFields), [initialValues]);
  const onCustomFieldDelete = useCallback(
    ({ arrayHelpers, index }: { arrayHelpers: FieldArrayRenderProps; index: number }) => {
      arrayHelpers.remove(index);
      const newCustomFields = [...initialCustomFields];
      newCustomFields.splice(index, 1);
      setInitialCustomFields(newCustomFields);
    },
    [initialCustomFields, setInitialCustomFields]
  );
  const onCustomFieldSetValue = useCallback(
    (field: string, value: unknown, resetTouched?: boolean | undefined) => {
      // @ts-ignore
      setFieldTouched(field, !resetTouched);
      // @ts-ignore
      setFieldValue(field, value);
    },
    [setFieldTouched, setFieldValue]
  );

  const isSmallerThanTablet = useIsSmallerThanTablet();
  if (!user) return <PleaseRelogin />;
  const canChangeRegularField =
    action !== 'UPDATE' || programPermissionHelper.canEditRegularField();
  const canSave = action !== 'UPDATE' || programPermissionHelper.canChangeAnything();

  const canManageAttachments = programPermissionHelper.canManageAttachments();
  const firstValidationError = getFirstValidationError(touched, errors);

  const isSocialPosting = isSocialPostingProgramType(values.type);
  return (
    <Form onSubmit={handleSubmit}>
      <div
        className="program-edit__header"
        style={{
          background: isCampaign
            ? hexToRGB(values.campaignColor!, PROGRAM_TYPE_BACKGROUND_ALPHA)
            : hexToRGB(values.type.color, PROGRAM_TYPE_BACKGROUND_ALPHA),
        }}
      >
        <div className="d-flex align-items-center w-100" style={{ minHeight: '38px' }}>
          <div className="program-edit__input--min-sized">
            {isCampaign ? (
              <CampaignColor
                campaignColor={
                  values.programKind === 'CAMPAIGN'
                    ? values.campaignColor!
                    : values.campaign!.campaignColor!
                }
                isDisabled={!canChangeRegularField}
                onValueChanged={(v) => setFieldValue('campaignColor', v)}
              />
            ) : (
              <ColoredSelect
                initialValue={values.type}
                coloredItems={programTypes.filter(
                  (type) => isSocialPosting === isSocialPostingProgramType(type)
                )}
                onValueChange={(value) => setFieldValue('type', value)}
                dataTestPrefix="program-type-select"
                disabled={!canChangeRegularField || isSocialPosting}
                hasError={!!errors.type && !!touched.type}
                initDummyItem={(id) => ({
                  id: id,
                  name: 'Unknown',
                  color: StyleVariables.unknownEntity,
                  role: null,
                })}
                menuZIndex={ModalDropdownMenuZIndex}
                forceFilterOptionDirtyHack={true}
                valueOptionLabelClassName="program-edit__program-type-value-option-label"
              />
            )}
          </div>
          {action === 'UPDATE' && !disabled && (
            <div className="ml-auto d-flex flex-row align-items-center">
              <CopyToClipboard text={AppRoutesHelper.getProgramDeepLinkUrl(initialValues.id)}>
                <DivButton
                  icon={<LinkIcon />}
                  onClick={() => {
                    notifySuccess({ notificationMsg: 'Link is copied.' });
                  }}
                  dataTest="program-edit__copy-link"
                  className="growegy-modal__header-button"
                />
              </CopyToClipboard>
              {addProgramPermissionHelper.canCreate(initialValues.programKind) &&
                !isSocialPosting && (
                  <DivButton
                    icon={<CopyIcon />}
                    onClick={handleCopyClick}
                    dataTest="program-edit__copy"
                    className="growegy-modal__header-button"
                  />
                )}
              {user.has(Permission.DELETE_PROGRAM) && (
                <DivButton
                  icon={<BinIcon />}
                  onClick={handleDeleteClick}
                  dataTest="program-edit__delete"
                  className="growegy-modal__header-button"
                />
              )}
              <div className="growegy-modal__header-separator-container">
                <DividerIcon />
              </div>
              <DivButton
                icon={<CloseIcon />}
                onClick={handleHideClick}
                dataTest="program-edit__close"
                className="growegy-modal__header-button"
              />
            </div>
          )}
          {action !== 'UPDATE' && !disabled && (
            <div className="ml-auto d-flex flex-row align-items-center">
              <DivButton
                icon={<CloseIcon />}
                onClick={handleHideClick}
                dataTest="program-edit__close"
                className="growegy-modal__header-button"
              />
            </div>
          )}
        </div>
        <ProgramEditName
          isInvalid={!!touched.name && !!errors.name}
          disabled={disabled}
          readonly={!canChangeRegularField}
          initialValue={initialValues.name}
          setValue={(v) => {
            setFieldTouched('name');
            setFieldValue('name', v, true);
          }}
          scrollToTaskId={scrollToTaskId}
          action={action}
          isCampaign={isCampaign}
          isSocialPosting={isSocialPosting}
        />
      </div>

      <div className="growegy-modal__form-body">
        <div>
          {!isSocialPosting && (
            <>
              <ProgramEditStatus
                value={values.status}
                setValue={(v) => setFieldValue('status', v)}
                isDisabled={disabled || !canChangeRegularField}
              />
              <ProgramEditOwner
                value={values.owner}
                setValue={(v) => setFieldValue('owner', v)}
                disabled={disabled || !programPermissionHelper.canChangeProgramOwner()}
              />
            </>
          )}
          {isSocialPosting && (
            <ProgramEditSocialPosting
              values={values}
              canChangeRegularField={canChangeRegularField}
              disabled={disabled}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
            />
          )}
          {!isSocialPosting && (
            <>
              {!isCampaign && (
                <ProgramEditCampaign
                  value={values.campaign}
                  setValue={(v) => setFieldValue('campaign', v)}
                  campaigns={programPermissionHelper.getCampaignOptions(campaigns)}
                  onShowCampaignClick={handleShowCampaignClick}
                  disabled={
                    disabled ||
                    (action === 'UPDATE' && !programPermissionHelper.canChangeCampaign())
                  }
                  required={
                    action === 'UPDATE'
                      ? !programPermissionHelper.canMakeStandalone()
                      : !addProgramPermissionHelper.canCreateStandaloneProgram()
                  }
                  hasError={!!errors.campaign && !!touched.campaign}
                />
              )}
              {isSmallerThanTablet && (
                <ProgramEditBudget
                  initialValue={initialValues.budget}
                  disabled={disabled}
                  readOnly={!canChangeRegularField}
                  setValue={(v) => {
                    setFieldTouched('budget');
                    setFieldValue('budget', v ?? '', true);
                  }}
                  isInvalid={!!touched.budget && !!errors.budget}
                />
              )}
              {!isCampaign && isSmallerThanTablet && (
                <ProgramEditVendor
                  initialValue={initialValues.vendor}
                  disabled={disabled}
                  readOnly={!canChangeRegularField}
                  setValue={(v) => {
                    setFieldTouched('vendor');
                    setFieldValue('vendor', v, true);
                  }}
                  isInvalid={!!touched.vendor && !!errors.vendor}
                />
              )}
            </>
          )}
          <ProgramEditDates
            initialValues={initialValues}
            values={values}
            disabled={disabled}
            readOnly={!canChangeRegularField}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            isStartInvalid={!!touched.startDateTime && !!errors.startDateTime}
            isEndInvalid={!!touched.endDateTime && !!errors.endDateTime}
          />
          {!isSocialPosting && (
            <>
              <Form.Group controlId="leads" className="growegy-form__row">
                <label className="growegy-form__label">Leads</label>
                <div className="d-flex">
                  <input
                    type="number"
                    name="actualLeads"
                    placeholder={canChangeRegularField ? 'Add actual' : ''}
                    value={values.actualLeads === null ? '' : values.actualLeads}
                    disabled={disabled}
                    readOnly={!canChangeRegularField}
                    onChange={(
                      e: React.ChangeEvent<
                        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
                      >
                    ) => {
                      const newValue = e.target.value ? +e.target.value : null;
                      setFieldTouched('actualLeads');
                      setFieldValue('actualLeads', newValue, true);
                    }}
                    className={clsx('program-edit__leads-input', {
                      'is-invalid': touched.actualLeads && errors.actualLeads,
                    })}
                    data-test="program-edit__actual-leads"
                  />
                  <ErrorMessage
                    component="div"
                    name="actualLeads"
                    className="invalid-feedback"
                    data-test="program-edit__actual-leads-validation-error"
                  />
                  <div
                    className="d-flex align-items-center"
                    style={{ margin: '0 12px', width: '16px' }}
                  >
                    <span>of</span>
                  </div>
                  <div>
                    <input
                      type="number"
                      name="leads"
                      placeholder={canChangeRegularField ? 'Add projected' : ''}
                      value={values.leads}
                      disabled={disabled}
                      readOnly={!canChangeRegularField}
                      onChange={(e) => {
                        setFieldTouched('leads');
                        handleChange(e);
                      }}
                      className={clsx('program-edit__leads-input', {
                        'is-invalid': touched.leads && errors.leads,
                      })}
                      data-test="program-edit__leads"
                    />
                    <ErrorMessage
                      component="div"
                      name="leads"
                      className="invalid-feedback"
                      data-test="program-edit__leads-validation-error"
                    />
                  </div>
                </div>
              </Form.Group>
              {!isSmallerThanTablet && (
                <ProgramEditBudget
                  initialValue={initialValues.budget}
                  disabled={disabled}
                  readOnly={!canChangeRegularField}
                  setValue={(v) => {
                    setFieldTouched('budget');
                    setFieldValue('budget', v ?? '', true);
                  }}
                  isInvalid={!!touched.budget && !!errors.budget}
                />
              )}
              {!isCampaign && !isSmallerThanTablet && (
                <ProgramEditVendor
                  initialValue={initialValues.vendor}
                  disabled={disabled}
                  readOnly={!canChangeRegularField}
                  setValue={(v) => {
                    setFieldTouched('vendor');
                    setFieldValue('vendor', v, true);
                  }}
                  isInvalid={!!touched.vendor && !!errors.vendor}
                />
              )}
              <FieldArray name="customFields">
                {(arrayHelpers) => (
                  <>
                    {initialCustomFields.map((customField, index) => (
                      <CustomFieldMemoised
                        formikValues={values}
                        key={`${customField.localId}`}
                        initialCustomField={customField}
                        index={index}
                        disabled={disabled}
                        readOnly={!canChangeRegularField}
                        errors={errors}
                        touched={touched}
                        arrayHelpers={arrayHelpers}
                        onDelete={onCustomFieldDelete}
                        handleBlur={handleBlurMemoized}
                        setFieldValue={onCustomFieldSetValue}
                      />
                    ))}

                    {!disabled && canChangeRegularField && (
                      <Form.Group className="growegy-form__row">
                        {!isSmallerThanTablet && <label className="growegy-form__label" />}
                        <DivButton
                          isGray={true}
                          className="common-table__add-new-btn"
                          icon={<PlusSmallIcon />}
                          text="Add new field"
                          dataTest="program-edit__add-custom-field"
                          onClick={() => {
                            const newCustomField = newProgramCustomField();
                            arrayHelpers.push(newCustomField);
                            setInitialCustomFields([...initialCustomFields, newCustomField]);
                          }}
                        />
                      </Form.Group>
                    )}
                  </>
                )}
              </FieldArray>
            </>
          )}
        </div>
        {!isSocialPosting && (
          <ProgramEditDescription
            initialValue={initialValues.notes}
            disabled={disabled}
            readOnly={!canChangeRegularField}
            setValue={(v) => {
              setFieldTouched('notes');
              setFieldValue('notes', v, true);
            }}
            isInvalid={!!touched.notes && !!errors.notes}
            isError={!!errors.notes && touched.notes}
            errorText={errors.notes}
            errorClassName="program-edit__input-error"
          />
        )}
        {!isCampaign && !isSocialPosting && (
          <Form.Group
            className={clsx('growegy-form__row', {
              'overflow-auto': isSmallerThanTablet,
            })}
          >
            <div data-test="program-edit__tasks" className="w-100">
              <FieldArray name="resolvedTasks">
                {(arrayHelpers) => (
                  <ProgramDragDropContext
                    programSummaries={[values]}
                    taskArrayHelpersFactory={() => arrayHelpers}
                  >
                    <Tasks
                      program={values}
                      values={values.resolvedTasks}
                      arrayHelpers={wrapArrayHelpers(arrayHelpers)}
                      scrollToTaskId={scrollToTaskId}
                      emphasizeFirstColumn={true}
                      isReadonly={false}
                      taskInFocusOrderId={taskInFocusOrderId}
                      onConvertTaskToProgram={handleConvertTaskToProgramClick}
                    />
                  </ProgramDragDropContext>
                )}
              </FieldArray>
            </div>
          </Form.Group>
        )}
        {isCampaign && (
          <CampaignProgramsTable
            campaign={values}
            onChanged={() => {
              setFieldTouched('isChangedCampaignPrograms');
              setFieldValue('isChangedCampaignPrograms', true, true);
            }}
          />
        )}
        {!isSocialPosting && (
          <div>
            <label className="growegy-form__section">Attachments</label>
            <Attachments
              attachments={values.attachments}
              programId={values.id}
              hasAddAccess={canManageAttachments}
              hasDeleteAccess={canManageAttachments}
            />
          </div>
        )}
      </div>
      <div className="program-edit__footer">
        <>
          {uploadProgress !== undefined && (
            <div className="program-edit__progress-bar">
              <ProgressBar
                striped
                variant="info"
                now={Math.min(Math.max(Math.floor(uploadProgress), 0), 100)}
                label={
                  uploadProgress > 10 ? (
                    <span className="font-weight-bold">{`${Math.min(
                      Math.max(Math.floor(uploadProgress), 0),
                      100
                    )}%`}</span>
                  ) : undefined
                }
              />
            </div>
          )}
          {createError && (
            <FooterError
              dataTest="program-edit__alert-warning"
              message={`Failed to create ${isCampaign ? 'campaign' : 'program'}: ${errorToString(
                createError
              )}`}
            />
          )}
          {updateError && (
            <>
              <FooterError
                dataTest="program-edit__alert-warning"
                message={`Failed to update ${isCampaign ? 'campaign' : 'program'}: ${errorToString(
                  updateError
                )}`}
              />
              <Button
                className="button-apply"
                onClick={handleRefetchClick}
                disabled={disabled}
                data-test="program-edit__refetch"
              >
                Refetch
              </Button>
            </>
          )}
          {deleteError && (
            <FooterError
              dataTest="program-edit__alert-warning"
              message={`Failed to delete program: ${errorToString(deleteError)}`}
            />
          )}
          {!isValid && firstValidationError && (
            <FooterError dataTest="program-edit__validation-error" message={firstValidationError} />
          )}
          <DivButton
            onClick={handleHideClick}
            isGray={true}
            dataTest="program-edit__cancel"
            className="div-button--cancel"
            text={canSave ? 'Cancel' : 'Close'}
          />
          {canSave && (
            <>
              <div data-tooltip-id={submitButtonTooltipId} data-tip-disable={false}>
                <Button
                  type="submit"
                  variant="primary"
                  className="button-apply"
                  disabled={disabled}
                  data-test="program-edit__apply"
                >
                  <span>
                    {action === 'UPDATE'
                      ? `Save ${
                          isCampaign ? 'campaign' : isSocialPosting ? 'social post' : 'program'
                        }`
                      : `Add ${
                          isCampaign ? 'campaign' : isSocialPosting ? 'social post' : 'program'
                        }`}
                  </span>
                </Button>
              </div>
            </>
          )}
        </>
      </div>
      <FormulaScopeUpdater />
    </Form>
  );
};
